import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import paymentService from "../../../services/paymentService";
import loadService from "../../../services/loadService";
import { convertFormDateToUTC5 } from "../../../utils/dateUtils";
import ReceivableForm from "./components/ReceivableForm";
import PayableForm from "./components/PayableForm";
import LoadSearch from "./components/LoadSearch";
import "./PaymentWizard.scss";

const PaymentWizard = ({ 
  isOpen = true, 
  onClose, 
  paymentType = 'receivable',
  mode = 'add',
  paymentData = null,
  onSuccess 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [showNotFound, setShowNotFound] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // React Hook Form for Receivable
  const receivableForm = useForm({
    defaultValues: {
      amount: "",
      dueDate: "",
      paymentMethod: "",
      notes: "",
      status: "pending"
    }
  });

  // React Hook Form for Payable
  const payableForm = useForm({
    defaultValues: {
      amount: "",
      scheduledDate: "",
      paymentMethod: "",
      quickPayFee: "",
      factoringFee: "",
      otherDeductions: "",
      deductionNotes: "",
      notes: "",
      status: "pending",
      accountNumber: "",
      routing: "",
      bank: ""
    }
  });

  const [images, setImages] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [existingPdfs, setExistingPdfs] = useState([]);

  const receivableStatuses = ['pending', 'invoiced', 'partial', 'paid', 'overdue', 'cancelled'];
  const payableStatuses = ['pending', 'scheduled', 'partial', 'paid', 'cancelled'];
  const paymentMethods = ['ACH', 'Wire', 'Check', 'Credit Card', 'Cash', 'Other'];

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (mode === 'edit' && paymentData) {
      if (paymentType === 'receivable') {
        receivableForm.reset({
          amount: paymentData.amount || "",
          dueDate: paymentData.dueDate ? new Date(paymentData.dueDate).toISOString().split('T')[0] : "",
          paymentMethod: paymentData.paymentMethod || "",
          notes: paymentData.notes || "",
          status: paymentData.status || "pending"
        });
        if (paymentData.images && Array.isArray(paymentData.images)) {
          setExistingImages(paymentData.images);
        }
        if (paymentData.pdfs && Array.isArray(paymentData.pdfs)) {
          setExistingPdfs(paymentData.pdfs);
        }
        if (paymentData.load) {
          setSelectedLoad(paymentData.load);
          setExpandedItem(paymentData.load._id || paymentData.load.id);
        }
      } else {
        const deductions = paymentData.deductions || {};
        payableForm.reset({
          amount: paymentData.amount || "",
          scheduledDate: paymentData.scheduledDate ? new Date(paymentData.scheduledDate).toISOString().split('T')[0] : "",
          paymentMethod: paymentData.paymentMethod || "",
          quickPayFee: deductions.quickPayFee || "",
          factoringFee: deductions.factoringFee || "",
          otherDeductions: deductions.otherDeductions || "",
          deductionNotes: deductions.deductionNotes || "",
          notes: paymentData.notes || "",
          status: paymentData.status || "pending",
          accountNumber: paymentData.accountNumber || "",
          routing: paymentData.routing || "",
          bank: paymentData.bank || ""
        });
        if (paymentData.images && Array.isArray(paymentData.images)) {
          setExistingImages(paymentData.images);
        }
        if (paymentData.pdfs && Array.isArray(paymentData.pdfs)) {
          setExistingPdfs(paymentData.pdfs);
        }
        if (paymentData.load) {
          setSelectedLoad(paymentData.load);
          setExpandedItem(paymentData.load._id || paymentData.load.id);
        }
      }
    }
  }, [mode, paymentData, paymentType, receivableForm, payableForm]);

  const searchLoads = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowNotFound(false);
      return;
    }
    setIsSearching(true);
    try {
      const response = await loadService.searchLoads({ q: query.trim(), limit: 10 });
      const loads = response?.data?.data || response?.data || [];
      setSearchResults(loads);
      setShowNotFound(loads.length === 0);
    } catch (err) {
      console.error("Error searching loads:", err);
      setSearchResults([]);
      setShowNotFound(true);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchLoads(searchQuery);
      } else {
        setSearchResults([]);
        setShowNotFound(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchLoads]);

  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    const lowerText = String(text).toLowerCase();
    const lowerQuery = query.toLowerCase();
    if (lowerText.includes(lowerQuery)) {
      const index = lowerText.indexOf(lowerQuery);
      return (
        <>
          {text.substring(0, index)}
          <span className="payment-wizard__highlight">
            {text.substring(index, index + query.length)}
          </span>
          {text.substring(index + query.length)}
        </>
      );
    }
    return text;
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setError("");
  };

  const handleSelectLoad = (load) => {
    setSelectedLoad(load);
    setExpandedItem(load._id || load.id);
    setSearchQuery("");
    setSearchResults([]);
    setShowNotFound(false);
  };

  const handleClosePaymentForm = () => {
    setExpandedItem(null);
    setSelectedLoad(null);
    setImages([]);
    setPdfs([]);
    setExistingImages([]);
    setExistingPdfs([]);
    if (paymentType === 'receivable') {
      receivableForm.reset({
        amount: "",
        dueDate: "",
        paymentMethod: "",
        notes: "",
        status: "pending"
      });
    } else {
      payableForm.reset({
        amount: "",
        scheduledDate: "",
        paymentMethod: "",
        quickPayFee: "",
        factoringFee: "",
        otherDeductions: "",
        deductionNotes: "",
        notes: "",
        status: "pending",
        accountNumber: "",
        routing: "",
        bank: ""
      });
    }
  };

  const calculateNetAmount = () => {
    const formValues = payableForm.watch();
    const amount = parseFloat(formValues.amount) || 0;
    const quickPay = parseFloat(formValues.quickPayFee) || 0;
    const factoring = parseFloat(formValues.factoringFee) || 0;
    const other = parseFloat(formValues.otherDeductions) || 0;
    return (amount - quickPay - factoring - other).toFixed(2);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages([...images, ...files]);
  };

  const handlePdfChange = (e) => {
    const files = Array.from(e.target.files);
    setPdfs([...pdfs, ...files]);
  };

  const removeImage = (index) => setImages(images.filter((_, i) => i !== index));
  const removePdf = (index) => setPdfs(pdfs.filter((_, i) => i !== index));
  const removeExistingImage = (index) => setExistingImages(existingImages.filter((_, i) => i !== index));
  const removeExistingPdf = (index) => setExistingPdfs(existingPdfs.filter((_, i) => i !== index));

  const handleSave = async (formData) => {
    setIsSubmitting(true);
    setError("");

    try {
      // Validate load selection for new payments
      if (mode === 'add' && !selectedLoad) {
        setError("Please select a load first");
        setIsSubmitting(false);
        return;
      }

      const loadId = selectedLoad?._id || selectedLoad?.id;
      if (mode === 'add' && (!loadId || (typeof loadId !== 'string' && typeof loadId !== 'object'))) {
        setError("Invalid load selected. Please select a valid load.");
        setIsSubmitting(false);
        return;
      }

      if (paymentType === 'receivable') {
        // In edit mode, only send editable fields: status, images, pdfs
        if (mode === 'edit' && paymentData) {
          const paymentId = paymentData._id || paymentData.id;
          if (!paymentId) {
            setError("Invalid payment ID");
            setIsSubmitting(false);
            return;
          }

          let data;
          if (images.length > 0 || pdfs.length > 0) {
            data = new FormData();
            data.append('status', formData.status);
            images.forEach((file) => data.append('images', file));
            pdfs.forEach((file) => data.append('pdfs', file));
          } else {
            data = { status: formData.status };
          }
          await paymentService.updateReceivable(paymentId, data);
        } else {
          // Add mode - full form
          const data = {
            ...(loadId && { load: typeof loadId === 'object' ? (loadId._id || loadId.id) : loadId }),
            amount: parseFloat(formData.amount),
            dueDate: formData.dueDate ? convertFormDateToUTC5(formData.dueDate) : undefined,
            paymentMethod: formData.paymentMethod || undefined,
            notes: formData.notes || undefined,
            status: formData.status
          };

          if (!data.amount || isNaN(data.amount) || data.amount <= 0) {
            setError("Please enter a valid amount");
            setIsSubmitting(false);
            return;
          }

          let submitData;
          if (images.length > 0 || pdfs.length > 0) {
            submitData = new FormData();
            Object.keys(data).forEach(key => {
              if (data[key] !== undefined) {
                submitData.append(key, data[key]);
              }
            });
            images.forEach((file) => submitData.append('images', file));
            pdfs.forEach((file) => submitData.append('pdfs', file));
            await paymentService.createReceivable(submitData);
          } else {
            await paymentService.createReceivable(data);
          }
        }
      } else {
        // In edit mode, only send editable fields: accountNumber, images, pdfs, routing, bank
        if (mode === 'edit' && paymentData) {
          const paymentId = paymentData._id || paymentData.id;
          if (!paymentId) {
            setError("Invalid payment ID");
            setIsSubmitting(false);
            return;
          }

          let data;
          if (images.length > 0 || pdfs.length > 0) {
            data = new FormData();
            if (formData.accountNumber) data.append('accountNumber', formData.accountNumber);
            if (formData.routing) data.append('routing', formData.routing);
            if (formData.bank) data.append('bank', formData.bank);
            images.forEach((file) => data.append('images', file));
            pdfs.forEach((file) => data.append('pdfs', file));
          } else {
            data = {
              ...(formData.accountNumber && { accountNumber: formData.accountNumber }),
              ...(formData.routing && { routing: formData.routing }),
              ...(formData.bank && { bank: formData.bank })
            };
          }
          await paymentService.updatePayable(paymentId, data);
        } else {
          // Add mode - full form
          const data = {
            ...(loadId && { load: typeof loadId === 'object' ? (loadId._id || loadId.id) : loadId }),
            amount: parseFloat(formData.amount),
            scheduledDate: formData.scheduledDate ? convertFormDateToUTC5(formData.scheduledDate) : undefined,
            paymentMethod: formData.paymentMethod || undefined,
            deductions: {
              quickPayFee: parseFloat(formData.quickPayFee) || 0,
              factoringFee: parseFloat(formData.factoringFee) || 0,
              otherDeductions: parseFloat(formData.otherDeductions) || 0,
              deductionNotes: formData.deductionNotes || undefined
            },
            notes: formData.notes || undefined,
            status: formData.status,
            ...(formData.accountNumber && { accountNumber: formData.accountNumber }),
            ...(formData.routing && { routing: formData.routing }),
            ...(formData.bank && { bank: formData.bank })
          };

          if (!data.amount || isNaN(data.amount) || data.amount <= 0) {
            setError("Please enter a valid amount");
            setIsSubmitting(false);
            return;
          }

          let submitData;
          if (images.length > 0 || pdfs.length > 0) {
            submitData = new FormData();
            Object.keys(data).forEach(key => {
              if (data[key] !== undefined) {
                if (key === 'deductions') {
                  submitData.append('deductions', JSON.stringify(data[key]));
                } else {
                  submitData.append(key, data[key]);
                }
              }
            });
            images.forEach((file) => submitData.append('images', file));
            pdfs.forEach((file) => submitData.append('pdfs', file));
            await paymentService.createPayable(submitData);
          } else {
            await paymentService.createPayable(data);
          }
        }
      }

      handleClosePaymentForm();
      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      console.error("Error saving payment:", err);
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to save payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    if (onClose) onClose();
  };

  const customerInfo = mode === 'edit' && paymentData?.customer 
    ? paymentData.customer 
    : (selectedLoad?.customer || paymentData?.load?.customer);

  const carrierInfo = mode === 'edit' && paymentData?.carrier 
    ? paymentData.carrier 
    : (selectedLoad?.carrier || paymentData?.load?.carrier);

  const loadInfo = mode === 'edit' && paymentData?.load 
    ? paymentData.load 
    : selectedLoad;

  const renderPaymentForm = () => {
    if (paymentType === 'receivable') {
      return (
        <ReceivableForm
          mode={mode}
          paymentData={paymentData}
          customerInfo={customerInfo}
          loadInfo={loadInfo}
          register={receivableForm.register}
          errors={receivableForm.formState.errors}
          watch={receivableForm.watch}
          setValue={receivableForm.setValue}
          handleSubmit={receivableForm.handleSubmit}
          images={images}
          pdfs={pdfs}
          existingImages={existingImages}
          existingPdfs={existingPdfs}
          handleImageChange={handleImageChange}
          handlePdfChange={handlePdfChange}
          removeImage={removeImage}
          removePdf={removePdf}
          removeExistingImage={removeExistingImage}
          removeExistingPdf={removeExistingPdf}
          receivableStatuses={receivableStatuses}
          paymentMethods={paymentMethods}
          error={error}
          isSubmitting={isSubmitting}
          onCancel={handleClosePaymentForm}
          onSave={handleSave}
        />
      );
    }
    return (
      <PayableForm
        mode={mode}
        paymentData={paymentData}
        carrierInfo={carrierInfo}
        loadInfo={loadInfo}
        register={payableForm.register}
        errors={payableForm.formState.errors}
        watch={payableForm.watch}
        setValue={payableForm.setValue}
        handleSubmit={payableForm.handleSubmit}
        images={images}
        pdfs={pdfs}
        existingImages={existingImages}
        existingPdfs={existingPdfs}
        handleImageChange={handleImageChange}
        handlePdfChange={handlePdfChange}
        removeImage={removeImage}
        removePdf={removePdf}
        removeExistingImage={removeExistingImage}
        removeExistingPdf={removeExistingPdf}
        payableStatuses={payableStatuses}
        paymentMethods={paymentMethods}
        calculateNetAmount={calculateNetAmount}
        error={error}
        isSubmitting={isSubmitting}
        onCancel={handleClosePaymentForm}
        onSave={handleSave}
      />
    );
  };

  return (
    <div className="payment-wizard">
      {isModalOpen && (
        <div className="modal show d-block">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5>
                  {mode === 'edit' ? 'Edit' : 'Add'} Payment {paymentType === 'receivable' ? 'Receivable' : 'Payable'}
                </h5>
                <button className="btn-close" onClick={handleClose}></button>
              </div>

              <div className="modal-body">
                <div className="payment-wizard__modal-content">
                  {mode === 'edit' && paymentData ? (
                    <div className="payment-wizard__results-container">
                      <div className="payment-wizard__result-wrapper payment-wizard__result-wrapper--expanded">
                        {renderPaymentForm()}
                      </div>
                    </div>
                  ) : (
                    <>
                      <LoadSearch
                        searchQuery={searchQuery}
                        onSearchChange={handleSearch}
                        isSearching={isSearching}
                        searchResults={searchResults}
                        showNotFound={showNotFound}
                        expandedItem={expandedItem}
                        paymentType={paymentType}
                        onSelectLoad={handleSelectLoad}
                        highlightMatch={highlightMatch}
                      />
                      {expandedItem && selectedLoad && (
                        <div className="payment-wizard__results-container">
                          <div className="payment-wizard__result-wrapper payment-wizard__result-wrapper--expanded">
                            {renderPaymentForm()}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentWizard;
